import React, { useEffect, useRef, useState, useContext } from "react";
import axios from "axios";
import {
  Send,
  Bot,
  Sparkles,
  RefreshCw,
} from "lucide-react";

import { AuthContext } from "../context/AuthContext";
import ChatMessage from "./ChatMessage";

const ChatBot = ({ isDrawer = false, onClose }) => {
  const { authData, backUrl } = useContext(AuthContext);

  const [messages, setMessages] = useState([
    {
      sender: "ai",
      text: "👋 Hello! I am **AI Assistant**, your secure enterprise HR co-pilot.\n\nAsk me about your team's live attendance, leave balances, payslips, company holidays, policies, and directory records.",
      display_type: "text",
      time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
    },
  ]);

  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [userScope, setUserScope] = useState(null);
  const messagesEndRef = useRef(null);

  // Thread isolation
  const [threadId, setThreadId] = useState(() => {
    const existing = localStorage.getItem("ai_chat_thread_id");
    if (existing) return existing;
    const id = "ai_thread_" + (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2));
    localStorage.setItem("ai_chat_thread_id", id);
    return id;
  });

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const currentTime = () => {
    return new Date().toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleResetSession = () => {
    const newId = "ai_thread_" + (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2));
    localStorage.setItem("ai_chat_thread_id", newId);
    setThreadId(newId);
    setMessages([
      {
        sender: "ai",
        text: "New conversation session initialized. All previous memory isolated.",
        display_type: "text",
        time: currentTime(),
      },
    ]);
  };

  const getAuthHeaders = () => {
    let token = authData?.access || authData?.token || authData?.access_token;
    if (!token) {
      try {
        const stored = JSON.parse(localStorage.getItem("authData") || "{}");
        token = stored?.access || stored?.token || stored?.access_token;
      } catch (e) {
        token = null;
      }
    }
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const getBaseUrl = () => {
    return backUrl !== undefined && backUrl !== null ? backUrl : "";
  };

  const sendMessage = async (customQuery) => {
    const queryToSend = customQuery || question;
    if (!queryToSend.trim()) return;

    const userMessage = {
      sender: "user",
      text: queryToSend,
      time: currentTime(),
    };

    setMessages((prev) => [...prev, userMessage]);
    if (!customQuery) setQuestion("");
    setLoading(true);

    try {
      const baseUrl = getBaseUrl();
      const headers = getAuthHeaders();

      const response = await axios.post(
        `${baseUrl}/api/chatbot/ai-chat/`,
        {
          question: queryToSend,
          thread_id: threadId,
        },
        { headers }
      );

      if (response.data.scope_info) {
        setUserScope(response.data.scope_info);
      }

      const aiMessage = {
        sender: "ai",
        text: response.data.answer,
        display_type: response.data.display_type || "text",
        data: response.data.data || {},
        sources: response.data.sources || [],
        tool_called: response.data.tool_called,
        time: currentTime(),
      };

      setMessages((prev) => [...prev, aiMessage]);
    } catch (error) {
      console.error("AI Assistant Chat Error:", error);
      // Fallback attempt to chat endpoint
      let handled = false;
      try {
        const baseUrl = getBaseUrl();
        const headers = getAuthHeaders();
        const fbRes = await axios.post(
          `${baseUrl}/api/chatbot/chat/`,
          {
            question: queryToSend,
            thread_id: threadId,
          },
          { headers }
        );
        if (fbRes.data && fbRes.data.success !== false && fbRes.data.answer) {
          setMessages((prev) => [
            ...prev,
            {
              sender: "ai",
              text: fbRes.data.answer,
              sources: fbRes.data.sources || [],
              display_type: "text",
              time: currentTime(),
            },
          ]);
          handled = true;
        }
      } catch (fbErr) {
        // Fallback failed
      }

      if (!handled) {
        const errorMsg =
          error.response?.data?.message ||
          error.response?.data?.detail ||
          error.message ||
          "Unable to connect to the AI Assistant server. Please verify you are logged in.";
        setMessages((prev) => [
          ...prev,
          {
            sender: "ai",
            text: `⚠️ ${errorMsg}`,
            display_type: "error",
            time: currentTime(),
          },
        ]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div
      style={{
        maxWidth: isDrawer ? "100%" : "960px",
        width: "100%",
        margin: isDrawer ? "0" : "20px auto",
        borderRadius: isDrawer ? "0px" : "16px",
        overflow: "hidden",
        background: "#0f172a",
        color: "#f8fafc",
        boxShadow: isDrawer ? "none" : "0 12px 40px rgba(0, 0, 0, 0.4)",
        border: isDrawer ? "none" : "1px solid rgba(255, 255, 255, 0.08)",
        display: "flex",
        flexDirection: "column",
        height: isDrawer ? "100%" : "750px",
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "16px 24px",
          background: "linear-gradient(90deg, #1e293b, #0f172a)",
          borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
          <div
            style={{
              width: "42px",
              height: "42px",
              borderRadius: "12px",
              background: "linear-gradient(135deg, #3b82f6, #6366f1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 0 15px rgba(99, 102, 241, 0.4)",
            }}
          >
            <Bot size={24} color="#fff" />
          </div>
          <div>
            <div style={{ fontSize: "17px", fontWeight: "700", color: "#f8fafc" }}>
              AI Assistant
            </div>
          </div>
        </div>

        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <button
            onClick={handleResetSession}
            title="Start New Thread Session"
            style={{
              padding: "8px 12px",
              borderRadius: "8px",
              background: "rgba(255, 255, 255, 0.06)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              color: "#94a3b8",
              fontSize: "12px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <RefreshCw size={14} />
            <span>New Chat</span>
          </button>
          {onClose && (
            <button
              onClick={onClose}
              title="Close Assistant"
              style={{
                padding: "6px 10px",
                borderRadius: "8px",
                background: "rgba(255, 255, 255, 0.06)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                color: "#94a3b8",
                fontSize: "15px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                lineHeight: 1,
              }}
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Messages Container */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "24px",
          background: "radial-gradient(ellipse at top, #1e293b 0%, #0f172a 100%)",
        }}
      >
        {messages.map((message, index) => (
          <ChatMessage key={index} message={message} />
        ))}

        {loading && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              color: "#60a5fa",
              fontSize: "13px",
              padding: "10px 16px",
              borderRadius: "12px",
              background: "rgba(59, 130, 246, 0.1)",
              border: "1px solid rgba(59, 130, 246, 0.2)",
              maxWidth: "340px",
              marginBottom: "15px",
            }}
          >
            <Sparkles size={16} className="animate-spin" />
            <span>AI Assistant is reasoning...</span>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Form */}
      <div
        style={{
          padding: "16px 20px",
          background: "#1e293b",
          borderTop: "1px solid rgba(255, 255, 255, 0.08)",
          display: "flex",
          gap: "12px",
          alignItems: "center",
        }}
      >
        <input
          type="text"
          placeholder="Ask an HR question (e.g. 'Show my team attendance' or 'What is my leave balance?')..."
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={loading}
          style={{
            flex: 1,
            padding: "14px 18px",
            fontSize: "14px",
            borderRadius: "12px",
            background: "#0f172a",
            border: "1px solid rgba(255, 255, 255, 0.15)",
            color: "#f8fafc",
            outline: "none",
          }}
        />

        <button
          onClick={() => sendMessage()}
          disabled={loading || !question.trim()}
          style={{
            background: "linear-gradient(135deg, #2563eb, #4f46e5)",
            color: "white",
            border: "none",
            padding: "14px 24px",
            cursor: loading || !question.trim() ? "not-allowed" : "pointer",
            borderRadius: "12px",
            fontWeight: "600",
            fontSize: "14px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            opacity: loading || !question.trim() ? 0.5 : 1,
            boxShadow: "0 4px 14px rgba(79, 70, 229, 0.4)",
          }}
        >
          <span>Send</span>
          <Send size={16} />
        </button>
      </div>
    </div>
  );
};

export default ChatBot;